import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

/**
 * Mobile visitors landing on `/` should skip the marketing page and go
 * straight to the login screen — the marketing site is desktop-optimized
 * and truck-fleet users typically already have accounts. Breakpoint matches
 * Tailwind's `md` (768px), same as the rest of the responsive layouts.
 */
const LandingOrLogin = () => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  if (isMobile || isNativeApp()) return <Navigate to="/login" replace />;
  return <Landing />;
};
import { isNativeApp, WebOnly } from './lib/native';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { BrandingProvider } from './contexts/BrandingContext';
import ProtectedRoute from './components/ProtectedRoute';
import ChangePasswordRequired from './pages/ChangePasswordRequired';
import VerificationRequired from './pages/VerificationRequired';
import VerifyEmail from './pages/VerifyEmail';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import CompanyAdminDashboard from './pages/CompanyAdminDashboard';
import DispatcherDashboard from './pages/DispatcherDashboard';
import DriverDashboard from './pages/DriverDashboard';
import DriverOnboarding from './pages/DriverOnboarding';
import DriverApplication from './pages/DriverApplication';
import DriversManagement from './pages/DriversManagement';
import DriverDetail from './pages/DriverDetail';
import LoadsManagement from './pages/LoadsManagement';
import Payroll from './pages/Payroll';
import PayrollDetail from './pages/PayrollDetail';
import Analytics from './pages/Analytics';
import Landing from './pages/Landing';
import SignUp from './pages/SignUp';
import Billing, { BillingSuccess, BillingCancel } from './pages/Billing';
import Leads from './pages/Leads';
import AssetsManagement from './pages/AssetsManagement';import AssetDetail from './pages/AssetDetail';
import FleetVehicles from './pages/FleetVehicles';
import IftaReport from './pages/IftaReport';
import EscrowManagement from './pages/EscrowManagement';
import DeferredDeductions from './pages/DeferredDeductions';
import DriverAssets from './pages/DriverAssets';
import TimeOffAndAdvances from './pages/TimeOffAndAdvances';
import ChatHub from './pages/ChatHub';
import TeamChat from './pages/TeamChat';
import FeedbackPage from './pages/FeedbackPage';
import FeedbackWidget from './components/FeedbackWidget';
import TelemetryPage from './pages/TelemetryPage';
import TelemetryTracker from './components/TelemetryTracker';
import LeaveApprovalsPage from './pages/LeaveApprovalsPage';
import W9AdminPage from './pages/W9AdminPage';
import Form1099Page from './pages/Form1099Page';
import StartTrial from './pages/StartTrial';
import Settings from './pages/Settings';
import CarrierTrustPage from './pages/CarrierTrustPage';
import TaxesExpenses from './pages/TaxesExpenses';
import DemoInviteAccept from './pages/DemoInviteAccept';
import SuperAdminDemoRequests from './pages/SuperAdminDemoRequests';
import SuperAdminAccessRequests from './pages/SuperAdminAccessRequests';
import SuperAdminDeliveryLogs from './pages/SuperAdminDeliveryLogs';
import SuperAdminEmailTemplates from './pages/SuperAdminEmailTemplates';
import SuperAdminLegal from './pages/SuperAdminLegal';
import SuperAdminConversion from './pages/SuperAdminConversion';
import TenderInbox from './pages/TenderInbox';
import SuperAdminSetup from './pages/SuperAdminSetup';
import FactoryReset from './pages/FactoryReset';
import SeedSampleTenant from './pages/SeedSampleTenant';
import DemoTenants from './pages/DemoTenants';
import DriverRequestsInbox from './pages/DriverRequestsInbox';
import CompanyReset from './pages/CompanyReset';
import ManageStaff from './pages/ManageStaff';
import PublicW9Form from './pages/PublicW9Form';
import CostSettingsPage from './pages/CostSettingsPage';
import FleetProfitDashboardPage from './pages/FleetProfitDashboardPage';
import TruckPnL from './pages/TruckPnL';
import BrokerPerformance from './pages/BrokerPerformance';
import Profile from './pages/Profile';
import AdminSettings from './pages/AdminSettings';
import AuditLogPage from './pages/AuditLogPage';
import Compliance from './pages/Compliance';
import MyDocuments from './pages/MyDocuments';
import MyPay from './pages/MyPay';
import PaystubDetail from './pages/PaystubDetail';
import CorrectionsInbox from './pages/CorrectionsInbox';
import SubscriptionSuspended from './pages/SubscriptionSuspended';
import SuperAdminSubscriptions from './pages/SuperAdminSubscriptions';
import SuperAdminCompanies from './pages/SuperAdminCompanies';
import Invoices from './pages/Invoices';
import LiveTracking from './pages/LiveTracking';
import Team from './pages/Team';
import Contracts from './pages/Contracts';
import ContractWizard from './pages/ContractWizard';
import ContractTemplateEditor from './pages/ContractTemplateEditor';
import PublicContractSign from './pages/PublicContractSign';
import AcceptTeamInvite from './pages/AcceptTeamInvite';
import AuditCompliance from './pages/AuditCompliance';
import ComplianceCenter from './pages/ComplianceCenter';
import Announcements from './pages/Announcements';
import MyAnnouncements from './pages/MyAnnouncements';
import Training from './pages/Training';
import MyTraining from './pages/MyTraining';
import PublicInvoice from './pages/PublicInvoice';
import PublicTrack from './pages/PublicTrack';
import CompanyBranding from './pages/CompanyBranding';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import './App.css';

const DashboardRouter = () => {
  const { user } = useAuth();

  if (!user) return null;

  switch (user.role) {
    case 'super_admin':
      return <SuperAdminDashboard />;
    case 'company_admin':
      return <CompanyAdminDashboard />;
    case 'dispatcher':
      return <DispatcherDashboard />;
    case 'driver':
      return <DriverDashboard />;
    default:
      return <div>Unknown role</div>;
  }
};

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <BrandingProvider>
          <BrowserRouter>
          <Toaster position="top-right" richColors closeButton expand={false} duration={4000} />
          <FeedbackWidget />
          <TelemetryTracker />
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/sign/:token" element={<PublicContractSign />} />
          <Route path="/carrier/:companyId" element={<CarrierTrustPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/change-password" element={<ProtectedRoute allowWhenBlocked><ChangePasswordRequired /></ProtectedRoute>} />
          <Route path="/verify-required" element={<ProtectedRoute allowWhenBlocked><VerificationRequired /></ProtectedRoute>} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/start-trial" element={<WebOnly><StartTrial /></WebOnly>} />
          <Route path="/request-demo" element={<WebOnly><Navigate to="/start-trial" replace /></WebOnly>} />
          <Route path="/demo-invite/:token" element={<WebOnly><DemoInviteAccept /></WebOnly>} />
          <Route path="/signup" element={<WebOnly><Navigate to="/start-trial" replace /></WebOnly>} />
          <Route path="/onboarding/:token" element={<DriverOnboarding />} />
          <Route
            path="/onboarding/application"
            element={
              <ProtectedRoute allowedRoles={['driver']}>
                <DriverApplication />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drivers"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher']}>
                <DriversManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/drivers/:driverId"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher']}>
                <DriverDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/loads"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher']}>
                <LoadsManagement />
              </ProtectedRoute>
            }
          />
          <Route path="/taxes" element={<ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher', 'accountant']}><TaxesExpenses initialTab="dashboard" /></ProtectedRoute>} />
          <Route path="/taxes/expenses" element={<ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher', 'accountant']}><TaxesExpenses initialTab="expenses" /></ProtectedRoute>} />
          <Route path="/taxes/import" element={<ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher', 'accountant']}><TaxesExpenses initialTab="import" /></ProtectedRoute>} />
          <Route path="/taxes/reimburse" element={<ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher', 'accountant']}><TaxesExpenses initialTab="reimburse" /></ProtectedRoute>} />
          <Route path="/taxes/reports" element={<ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher', 'accountant']}><TaxesExpenses initialTab="reports" /></ProtectedRoute>} />
          <Route path="/taxes/assets" element={<ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher', 'accountant']}><TaxesExpenses initialTab="assets" /></ProtectedRoute>} />
          <Route path="/taxes/audit" element={<ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher', 'accountant']}><TaxesExpenses initialTab="audit" /></ProtectedRoute>} />
          <Route path="/taxes/settings" element={<ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher', 'accountant']}><TaxesExpenses initialTab="settings" /></ProtectedRoute>} />
          <Route
            path="/payroll"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher']}>
                <Payroll />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payroll/:payrollId"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher']}>
                <PayrollDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher', 'driver']}>
                <Analytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                <WebOnly to="/dashboard"><Billing /></WebOnly>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/success"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                <WebOnly to="/dashboard"><BillingSuccess /></WebOnly>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/cancel"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                <WebOnly to="/dashboard"><BillingCancel /></WebOnly>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/leads"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <Leads />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assets"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher']}>
                <AssetsManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assets/:assetId"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher']}>
                <AssetDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fleet/vehicles"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher']}>
                <FleetVehicles />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ifta"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                <IftaReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/escrow"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                <EscrowManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/deferred-deductions"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                <DeferredDeductions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-assets"
            element={
              <ProtectedRoute allowedRoles={['driver']}>
                <DriverAssets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher', 'driver']}>
                <ChatHub />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team-chat"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher', 'driver']}>
                <TeamChat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/time-off"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'driver']}>
                <TimeOffAndAdvances />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-leave"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'driver']}>
                <TimeOffAndAdvances section="leave" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave-requests"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher']}>
                <LeaveApprovalsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/w9"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                <W9AdminPage />
              </ProtectedRoute>
            }
          />
          <Route path="/w9/complete/:token" element={<PublicW9Form />} />
          <Route path="/pay/:invoiceId" element={<PublicInvoice />} />
          <Route path="/track/:token" element={<PublicTrack />} />
          <Route path="/team/accept/:token" element={<AcceptTeamInvite />} />
          <Route
            path="/admin/form-1099"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                <Form1099Page />
              </ProtectedRoute>
            }
          />
          <Route path="/loads/amazon-import" element={<Navigate to="/loads" replace />} />
          <Route
            path="/admin/access-requests"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminAccessRequests />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/demo-requests"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminDemoRequests />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/delivery-logs"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminDeliveryLogs />
              </ProtectedRoute>
            }
          />
          <Route path="/admin/email-templates" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminEmailTemplates /></ProtectedRoute>} />
          <Route path="/admin/legal" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminLegal /></ProtectedRoute>} />
          <Route path="/admin/conversion" element={<ProtectedRoute allowedRoles={['super_admin']}><SuperAdminConversion /></ProtectedRoute>} />
          <Route path="/tenders" element={<ProtectedRoute allowedRoles={['company_admin', 'dispatcher', 'super_admin']}><TenderInbox /></ProtectedRoute>} />
          <Route
            path="/admin/setup"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminSetup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/factory-reset"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <FactoryReset />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/seed-tenant"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SeedSampleTenant />
              </ProtectedRoute>
            }
          />
          <Route
            path="/requests"
            element={
              <ProtectedRoute allowedRoles={['company_admin', 'dispatcher', 'super_admin']}>
                <DriverRequestsInbox />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/demo-tenants"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <DemoTenants />
              </ProtectedRoute>
            }
          />
          <Route
            path="/company/reset"
            element={
              <ProtectedRoute allowedRoles={['company_admin']}>
                <CompanyReset />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/manage-staff"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                <ManageStaff />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cost-settings"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                <CostSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/profit-dashboard"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                <FleetProfitDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-advances"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'driver']}>
                <TimeOffAndAdvances section="advance" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-pay"
            element={
              <ProtectedRoute allowedRoles={['driver', 'dispatcher', 'company_admin', 'accountant']}>
                <MyPay />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-pay/:entryId"
            element={
              <ProtectedRoute allowedRoles={['driver', 'dispatcher', 'company_admin', 'accountant']}>
                <PaystubDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payroll/corrections"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                <CorrectionsInbox />
              </ProtectedRoute>
            }
          />
          <Route
            path="/truck-pnl"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher', 'driver']}>
                <TruckPnL />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brokers"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher', 'driver']}>
                <BrokerPerformance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher', 'driver']}>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route path="/settings" element={<ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher', 'driver']}><Settings /></ProtectedRoute>} />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                <AdminSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-log"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher']}>
                <AuditLogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/feedback-inbox"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                <FeedbackPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/telemetry"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin']}>
                <TelemetryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/compliance"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'company_admin', 'dispatcher']}>
                <Compliance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-documents"
            element={
              <ProtectedRoute allowedRoles={['driver']}>
                <MyDocuments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/subscriptions"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminSubscriptions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/companies"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminCompanies />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live-tracking"
            element={
              <ProtectedRoute allowedRoles={['company_admin', 'dispatcher', 'driver']}>
                <LiveTracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute allowedRoles={['company_admin', 'dispatcher']}>
                <Invoices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute allowedRoles={['company_admin', 'super_admin']}>
                <Team />
              </ProtectedRoute>
            }
          />
          <Route path="/contracts" element={<ProtectedRoute allowedRoles={['company_admin', 'dispatcher', 'driver']}><Contracts /></ProtectedRoute>} />
          <Route path="/contracts/templates/:id" element={<ProtectedRoute allowedRoles={['company_admin']}><ContractTemplateEditor /></ProtectedRoute>} />
          <Route path="/contracts/:id" element={<ProtectedRoute allowedRoles={['company_admin', 'dispatcher', 'driver']}><ContractWizard /></ProtectedRoute>} />
          <Route
            path="/compliance-center"
            element={
              <ProtectedRoute allowedRoles={['company_admin', 'super_admin']}>
                <ComplianceCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/announcements"
            element={
              <ProtectedRoute allowedRoles={['company_admin', 'super_admin']}>
                <Announcements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-announcements"
            element={
              <ProtectedRoute allowedRoles={['company_admin', 'dispatcher', 'driver']}>
                <MyAnnouncements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/training"
            element={
              <ProtectedRoute allowedRoles={['company_admin', 'super_admin']}>
                <Training />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-training"
            element={
              <ProtectedRoute allowedRoles={['company_admin', 'dispatcher', 'driver']}>
                <MyTraining />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/compliance"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <AuditCompliance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/company/branding"
            element={
              <ProtectedRoute allowedRoles={['company_admin']}>
                <CompanyBranding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suspended"
            element={
              <ProtectedRoute allowWhenBlocked>
                <SubscriptionSuspended />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardRouter />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<LandingOrLogin />} />
        </Routes>
      </BrowserRouter>
        </BrandingProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
